/**
 * @fileoverview Componente raiz responsável por inicializar tema, providers globais, estilos e rotas do frontend.
 */

import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import "./styles/toast.css";
import { QueryClient, QueryClientProvider } from "react-query";
import "./utils/toastConfig";
import {enUS, ptBR, esES} from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';
import { QueuesProvider } from "./context/Queues/QueuesContext";
import { BillingProvider } from "./context/Billing/BillingContext";

import Routes from "./routes";
import ErrorBoundary from "./components/ErrorBoundary";
import AutoRefreshToken from "./components/AutoRefreshToken";
import SharedBoard, { isSharedBoardPath } from "./pages/SharedBoard";

const queryClient = new QueryClient();

const App = () => {
    const [locale, setLocale] = useState();

    const preferredTheme = window.localStorage.getItem("preferredTheme");
    // Default to dark mode when no preference is saved
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : "dark");

    // CSS global para forçar cor verde no badge de notificação
    useEffect(() => {
        const globalStyles = `
          /* Restringe a cor SÓ ao badge de notificação */
          #notification-badge .MuiBadge-badge,
          #notification-badge .MuiBadge-colorPrimary,
          #notification-badge .MuiBadge-anchorOriginTopRightRectangle {
            background-color: #6EC263 !important;
            color: white !important;
          }

          /* Remove qualquer estilização agressiva de badges globais */
          .chat-badge-reset .MuiBadge-badge {
            background: transparent !important;
            color: inherit !important;
            box-shadow: none !important;
          }

          /* Destaque ao navegar para mensagem citada — pulso duplo pra deixar
             claro que o usuário "chegou" depois de um jump (em vez de um scroll
             contínuo). 1.5s total, suave em ambos os modos. */
          .message-highlight {
            animation: msgFlash 1.5s ease-out 2;
            border-radius: 8px;
          }
          @keyframes msgFlash {
            0%   { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); }
            40%  { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
            100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
          }

          /* GLASSMORPHISM NO DRAWER - IGUAL AO HEADER */
          ${mode === "dark" ? `
          .MuiDrawer-paper,
          .MuiDrawer-paperAnchorLeft,
          .MuiDrawer-paperAnchorDockedLeft,
          .MuiPaper-root.MuiDrawer-paper {
            background: linear-gradient(135deg, #0A0A0A 0%, #18181B 100%) !important;
            border-right: 1px solid rgba(91, 124, 255, 0.2) !important;
          }
          ` : ''}

          /* Corrigir background do TicketHeader para respeitar tema */
          .makeStyles-ticketHeader-103.MuiPaper-root {
            background-color: ${mode === "light" ? "#EEE" : "#18181B"} !important;
          }

          /* Garantir que Cards respeitem o tema */
          .MuiCard-root.makeStyles-ticketHeader-103 {
            background-color: ${mode === "light" ? "#EEE" : "#18181B"} !important;
          }

          /* Estilização moderna da scrollbar global */
          * {
            scrollbar-width: thin;
            scrollbar-color: ${mode === "light" ? "rgba(138, 138, 138, 0.57)" : "rgba(54, 54, 54, 0.95)"} transparent;
          }

          *::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }

          *::-webkit-scrollbar-track {
            background: transparent;
          }

          *::-webkit-scrollbar-thumb {
            background-color: ${mode === "light" ? "rgba(0, 0, 0, 0.57)" : "rgba(255, 255, 255, 0.35)"};
            border-radius: 3px;
          }

          *::-webkit-scrollbar-thumb:hover {
            background-color: ${mode === "light" ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.5)"};
          }

          *::-webkit-scrollbar-corner {
            background: transparent;
          }

          /* Aplicar scrollbar nos diálogos */
          .MuiDialog-paper,
          .MuiDialogContent-root {
            scrollbar-width: thin;
            scrollbar-color: ${mode === "light" ? "rgba(0, 0, 0, 0.57)" : "rgba(255, 255, 255, 0.35)"} transparent;
          }

          /* Estilo de autofill para inputs (login e outros formulários) */
          .MuiOutlinedInput-input:-webkit-autofill,
          .MuiFilledInput-input:-webkit-autofill,
          .MuiInput-input:-webkit-autofill,
          input:-webkit-autofill {
            caret-color: ${mode === "light" ? "#000" : "#fff"};
            border-radius: inherit;
            -webkit-box-shadow: 0 0 0 100px ${mode === "light" ? "#fafafa" : "#262626"} inset !important;
            -webkit-text-fill-color: ${mode === "light" ? "#000" : "#fff"} !important;
            transition: background-color 5000s ease-in-out 0s;
          }

          .MuiOutlinedInput-input:-webkit-autofill:hover,
          .MuiOutlinedInput-input:-webkit-autofill:focus {
            -webkit-box-shadow: 0 0 0 100px ${mode === "light" ? "#fafafa" : "#262626"} inset !important;
          }
        `;

        const style = document.createElement('style');
        style.textContent = globalStyles;
        document.head.appendChild(style);

        return () => {
            // Cleanup: remove o style quando o componente for desmontado
            if (style.parentNode) {
                style.parentNode.removeChild(style);
            }
        };
    }, [mode]);

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createTheme(
        {
            typography: {
                fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
            },
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '6px',
                    height: '6px',
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.57)" : "rgba(255, 255, 255, 0.35)",
                    borderRadius: "3px",
                },
                "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-corner": {
                    backgroundColor: "transparent",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "6px",
                    height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                    borderRadius: "3px",
                },
                "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-corner": {
                    backgroundColor: "transparent",
                },
            },
            palette: {
                type: mode,
                primary: { main: mode === "light" ? "#3D5CFD" : "#5B7CFF" },
                secondary: { main: mode === "light" ? "#3D5CFD" : "#5B7CFF" },
                success: { main: "#3D5CFD" },
                buttonHover: mode === "light" ? "#2A4BDB" : "#4A63E8",
                datePicker: {
                    color: mode === "light" ? "#000" : "#F5F5F5",
                    borderColor: mode === "light" ? "#000" : "#404040",
                    iconColor: mode === "light" ? "#000" : "#F5F5F5"
                },
                background: {
                    paper: mode === "light" ? "#fff" : "#18181B",
                    default: mode === "light" ? "#fafafa" : "#0A0A0A"
                },
                textPrimary: mode === "light" ? "#000" : "#F5F5F5",
                borderPrimary: mode === "light" ? "#682EE3" : "#404040",
                dark: { main: mode === "light" ? "#000000" : "#E5E5E5" },
                light: { main: mode === "light" ? "#F3F3F3" : "#18181B" },
                tabHeaderBackground: mode === "light" ? "#FFFFFF" : "#0A0A0A",
                optionsBackground: mode === "light" ? "#fafafa" : "#0A0A0A",
				options: mode === "light" ? "#fafafa" : "#0A0A0A",
				fontecor: mode === "light" ? "#128c7e" : "#E5E5E5",
                fancyBackground: mode === "light" ? "#ffffff" : "linear-gradient(135deg, #0A0A0A 0%, #18181B 100%)",
				bordabox: mode === "light" ? "#eee" : "#27272A",
				newmessagebox: mode === "light" ? "#eee" : "#18181B",
				inputdigita: mode === "light" ? "#fff" : "#18181B",
				contactdrawer: mode === "light" ? "#fff" : "#18181B",
				announcements: mode === "light" ? "#ededed" : "#18181B",
				login: mode === "light" ? "#fff" : "#0A0A0A",
				announcementspopover: mode === "light" ? "#fff" : "#18181B",
				chatlist: mode === "light" ? "#eee" : "#18181B",
				boxlist: mode === "light" ? "#ededed" : "#18181B",
				boxchatlist: mode === "light" ? "#ededed" : "#18181B",
                total: mode === "light" ? "#fff" : "#18181B",
                messageIcons: mode === "light" ? "grey" : "#A1A1AA",
                inputBackground: mode === "light" ? "#FFFFFF" : "#18181B",
                topBar: "linear-gradient(135deg, #0A0A0A 0%, #18181B 100%)",
				boxticket: mode === "light" ? "#fff" : "#18181B",
				campaigntab: mode === "light" ? "#ededed" : "#18181B",
				mediainput: mode === "light" ? "#ededed" : "#18181B",
            },
            overrides: {
                MuiButton: {
                    root: {
                        fontWeight: 500,
                    },
                },
                MuiDrawer: {
                    paper: {
                        background: mode === "dark"
                            ? 'linear-gradient(135deg, #0A0A0A 0%, #18181B 100%) !important'
                            : undefined,
                        borderRight: mode === "dark" ? '1px solid rgba(91, 124, 255, 0.2) !important' : undefined,
                    },
                },
                MuiTypography: {
                    colorPrimary: {
                        color: `${mode === "light" ? "#000" : "#eee"} !important`,
                    },
                },
                MuiTextField: {
                    root: {
                        '& .MuiInputLabel-root': {
                            color: mode === "light" ? "#000" : "#eee",
                        },
                        '& .MuiOutlinedInput-root': {
                            color: mode === "light" ? "#000" : "#eee",
                            '& fieldset': {
                                borderColor: mode === "light" ? "#000" : "#eee",
                            },
                            '&:hover fieldset': {
                                borderColor: mode === "light" ? "#000" : "#eee",
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: mode === "light" ? "#000" : "#eee",
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: mode === "light" ? "#000" : "#eee",
                        },
                        '& .MuiSvgIcon-root': {
                            color: mode === "light" ? "#000" : "#eee",
                        },
                    },
                },
            },
            mode,
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale = i18nlocale?.substring(0, 2) ?? 'pt';

        if (browserLocale === "pt"){
            setLocale(ptBR);
        }else if( browserLocale === "en" ) {
            setLocale(enUS)
        }else if( browserLocale === "es" )
            setLocale(esES)

    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);


    // O quadro do CRM compartilhado por link (sem login) fica fora dos
    // providers do app logado: sem sessão, socket, filas nem cobrança. Um
    // token velho de quem já usou o Sttelar neste navegador não o leva ao /login.
    if (isSharedBoardPath()) {
        return (
            <ColorModeContext.Provider value={{ colorMode }}>
                <ThemeProvider theme={theme}>
                    <ErrorBoundary>
                        <SharedBoard />
                    </ErrorBoundary>
                </ThemeProvider>
            </ColorModeContext.Provider>
        );
    }

    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                    <QueuesProvider>
                      <BillingProvider>
                        <AutoRefreshToken />
                        <ErrorBoundary>
                          <Routes />
                        </ErrorBoundary>
                      </BillingProvider>
                    </QueuesProvider>
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
